import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RootState, AppDispatch } from '../../../../store';
import { fetchTables, fetchSchemas, tableMapKey, tableName } from '../../../../store/connectionsSlice';
import { createDefinition, updateDefinition, fetchSamTypes, DataDefinition, DataDefinitionType, SamType } from '../../../../store/dataDefinitionsSlice';
import ServerFileBrowserDialog from './ServerFileBrowserDialog';
import { be1Fetch } from '../../../../config/api';
import { typeLabelOf } from '../../nodeCatalog';
import { useTranslation } from 'react-i18next';
import { errorText } from '../../../../services/errorText';

interface Props {
  type: DataDefinitionType;
  folderPath: string;
  onClose: () => void;
  /** 저장된 정의를 바로 쓰고 싶을 때 (마법사에서 만든 즉시 선택) */
  onCreated?: (item: any) => void;
  /** 서버 폴더 탐색 등에서 미리 채워서 열 때 (경로·SAM유형) */
  initial?: { filepath?: string; samTypeId?: string; name?: string };
  /** 있으면 **수정** — 이 정의의 값으로 폼을 채우고, 저장하면 같은 id 로 고친다 */
  editing?: DataDefinition;
  /** 수정을 저장한 뒤 (이 정의를 쓰는 노드로 퍼뜨리는 일을 부르는 쪽이 한다) */
  onSaved?: (item: DataDefinition) => void;
}

/**
 * '데이터 정의' 관리 화면에서 특정 폴더 아래에 새 SAM파일/DB테이블 정의를 만드는 폼.
 * 저장 시 서버가 실제 파일/테이블을 조회해 레이아웃(컬럼)을 함께 저장한다.
 */
const NewDataDefinitionDialog: React.FC<Props> = ({ type, folderPath, onClose, onCreated, initial, editing, onSaved }) => {
  //: 수정이면 저장된 정의의 값으로 시작한다
  const es: any = editing?.source || {};
  const { t } = useTranslation();
  const dispatch = useDispatch<AppDispatch>();
  const connections = useSelector((state: RootState) => state.connections.list);
  const tableMap = useSelector((state: RootState) => state.connections.tableMap);
  const schemaMap = useSelector((state: RootState) => state.connections.schemaMap);
  const defaultSchema = useSelector((state: RootState) => state.connections.defaultSchema);

  const [name, setName] = useState(editing?.name || initial?.name || '');
  const [filepath, setFilepath] = useState(es.filepath || initial?.filepath || '');
  const [hasHeader, setHasHeader] = useState<boolean>(es.hasHeader ?? true);
  const [delimiter, setDelimiter] = useState(es.delimiter || ',');
  // SAM 형식 — 자동 식별(sniff)로 초깃값을 채우고, 미리보기로 검증한다
  const [samFormat, setSamFormat] = useState<'delimited' | 'fixed'>(es.format || 'delimited');
  const [samEncoding, setSamEncoding] = useState(es.encoding || 'UTF-8');
  const [fieldWidths, setFieldWidths] = useState(es.fieldWidths || '');
  const [fieldNames, setFieldNames] = useState(es.fieldNames || '');
  const [sniffing, setSniffing] = useState(false);
  // SAM유형 — 고르면 형식 옵션·레이아웃이 유형에서 오고, 정의에는 경로만 입력한다
  const samTypes = useSelector((state: RootState) => state.dataDefinitions.samTypes);
  const samProfiles = useSelector((state: RootState) => state.dataDefinitions.samProfiles);
  const [samTypeId, setSamTypeId] = useState(es.samTypeId || initial?.samTypeId || '');
  const selType: SamType | undefined = samTypes.find((x) => x.id === samTypeId);
  const selProfile = selType ? samProfiles.find((x) => x.id === selType.profileId) : undefined;
  useEffect(() => { if (type === 'sam') dispatch(fetchSamTypes()); }, [type, dispatch]);
  // 유형을 고르면 미리보기 옵션을 유형 기준으로 맞춘다
  useEffect(() => {
    if (!selType || !selProfile) return;
    setSamFormat(selProfile.format);
    setDelimiter(selProfile.delimiter);
    setHasHeader(selProfile.hasHeader);
    setSamEncoding(selProfile.encoding);
    setFieldWidths(selType.fieldWidths || '');
    setFieldNames((selType.columns || []).map((c) => c.name).join(','));
  }, [samTypeId]);   // eslint-disable-line react-hooks/exhaustive-deps
  // 자동 식별 결과와 일치하는 커스텀구분유형 힌트
  const matchedProfile = samProfiles.find((pr) =>
    pr.format === samFormat && pr.encoding === samEncoding && pr.hasHeader === hasHeader &&
    (pr.format === 'fixed' || pr.delimiter === delimiter));
  const [preview, setPreview] = useState<{ columns: string[]; rows: string[][]; warning?: string | null } | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [connection, setConnection] = useState(es.connection || '');
  const [schema, setSchema] = useState(es.schema || '');
  const [table, setTable] = useState(es.table || '');
  const [browserOpen, setBrowserOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /** 파일을 고르면 서버가 인코딩·구분자·고정폭·헤더를 추정해 초깃값을 채운다 (미리보기로 확인 후 수정 가능) */
  const runSniff = async (path: string) => {
    if (!path.trim()) return;
    if (samTypeId) return;             // SAM유형이 형식을 정한다 — 자동 식별로 덮지 않는다
    setSniffing(true);
    try {
      const r = await be1Fetch(`/api/fs/sniff?path=${encodeURIComponent(path.trim())}`);
      if (!r.ok) return;
      const d = await r.json();
      if (d.encoding) setSamEncoding(d.encoding);
      if (d.format) setSamFormat(d.format);
      if (d.delimiter) setDelimiter(d.delimiter);
      if (typeof d.hasHeader === 'boolean') setHasHeader(d.hasHeader);
      if (d.format === 'fixed' && d.lineBytes && !fieldWidths) setFieldWidths(String(d.lineBytes));
    } catch { /* 식별 실패해도 수동 입력으로 진행 */ } finally { setSniffing(false); }
  };

  // 옵션이 바뀌면 미리보기를 다시 그린다 — 사용자가 옵션의 정확성을 눈으로 판단하는 통로
  useEffect(() => {
    if (type !== 'sam' || !filepath.trim()) { setPreview(null); return; }
    const timer = window.setTimeout(async () => {
      try {
        setPreviewErr(null);
        const r = await be1Fetch('/api/fs/sam-preview', {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: filepath.trim(), encoding: samEncoding, format: samFormat,
            delimiter, hasHeader, fieldWidths, fieldNames, limit: 10 }),
        });
        const d = await r.json();
        if (!r.ok) throw new Error(errorText(d, t('datadef:new.previewFailed')));
        setPreview(d);
      } catch (e) { setPreview(null); setPreviewErr((e as Error).message); }
    }, 400);
    return () => window.clearTimeout(timer);
  }, [type, filepath, samEncoding, samFormat, delimiter, hasHeader, fieldWidths, fieldNames, t]);

  // 커넥션을 고르면 그 계정에서 보이는 스키마를 먼저 읽고, 기본 스키마를 골라 둔다
  useEffect(() => {
    if (type === 'db' && connection && !schemaMap[connection]) dispatch(fetchSchemas(connection));
  }, [type, connection, schemaMap, dispatch]);

  useEffect(() => {
    if (type === 'db' && connection && !schema && defaultSchema[connection]) setSchema(defaultSchema[connection]);
  }, [type, connection, schema, defaultSchema]);

  useEffect(() => {
    if (type === 'db' && connection && !tableMap[tableMapKey(connection, schema)]) {
      dispatch(fetchTables({ connection_id: connection, schema }));
    }
  }, [type, connection, schema, tableMap, dispatch]);

  const canSave =
    name.trim().length > 0 &&
    (type === 'sam'
      ? filepath.trim().length > 0 && (samFormat !== 'fixed' || fieldWidths.trim().length > 0)
      : !!(connection && schema && table));

  const handleSave = async () => {
    setSaving(true);
    setError(null);
    try {
      const source =
        type === 'sam'
          ? (samTypeId
              ? { filepath: filepath.trim(), samTypeId }
              : { filepath: filepath.trim(), hasHeader, delimiter: delimiter || ',',
                  format: samFormat, encoding: samEncoding,
                  fieldWidths: fieldWidths.trim(), fieldNames: fieldNames.trim() })
          : { connection, schema, table };
      if (editing) {
        const saved = await dispatch(updateDefinition({ id: editing.id, name: name.trim(), source })).unwrap();
        onClose();
        onSaved?.(saved);
        return;
      }
      const r = await dispatch(createDefinition({ path: folderPath, name: name.trim(), source }));
      if ('error' in r && r.error) throw new Error((r.error as any).message || t('datadef:common.saveFailed'));
      if (onCreated && (r as any).payload) onCreated((r as any).payload);
      onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-black bg-opacity-30">
      <div className={`bg-white rounded shadow-lg ${type === 'sam' ? 'w-[700px]' : 'w-[420px]'} max-w-[95vw] p-4 max-h-[88vh] overflow-auto`}>
        <h3 className="text-sm font-semibold text-gray-900 mb-3">
          {editing
            ? t('datadef:new.editTitle', { kind: type === 'sam' ? typeLabelOf('csvData') : typeLabelOf('rdbData'), name: editing.name })
            : t('datadef:new.title', { kind: type === 'sam' ? typeLabelOf('csvData') : typeLabelOf('rdbData'), folder: folderPath })}
        </h3>

        <label className="block text-xs text-gray-500 mb-1">{t('common:ui.name')}</label>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-3"
          placeholder={type === 'sam' ? t('datadef:new.samPh') : t('datadef:new.tablePh')}
        />

        {type === 'sam' ? (
          <>
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-gray-500 shrink-0">{t('datadef:new.samType')}</span>
              <select value={samTypeId} onChange={(e) => setSamTypeId(e.target.value)}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1">
                <option value="">{t('datadef:new.noType')}</option>
                {samTypes.map((x) => <option key={x.id} value={x.id}>{x.name}</option>)}
              </select>
            </div>
            {selType && selProfile && (
              <div className="mb-2 text-[0.6875rem] text-blue-700 bg-blue-50 border border-blue-200 rounded px-2 py-1">
                {t('datadef:new.typeInfo1')}<b>{selType.name}</b> — {selProfile.format === 'fixed' ? t('datadef:sam.fixed') : t('datadef:sam.delimiterIs', { d: selProfile.delimiter === String.fromCharCode(9) ? 'TAB' : selProfile.delimiter })}
                {t('datadef:new.typeInfo2', { header: selProfile.hasHeader ? t('datadef:new.yes') : t('datadef:new.no'), encoding: selProfile.encoding, n: selType.columns.length })}<b>{t('datadef:new.pathOnly')}</b>{t('datadef:new.typeInfo3')}
              </div>
            )}
            <label className="block text-xs text-gray-500 mb-1">{t('datadef:new.filePath')}</label>
            <div className="flex gap-2 mb-3">
              <input
                value={filepath}
                onChange={(e) => setFilepath(e.target.value)}
                onBlur={() => runSniff(filepath)}
                className="flex-1 text-sm border border-gray-300 rounded px-2 py-1"
                placeholder={t('datadef:new.filePathPh')}
              />
              <button
                type="button"
                className="px-2 py-1 text-xs border border-gray-300 rounded hover:bg-gray-50"
                onClick={() => setBrowserOpen(true)}
              >
                {t('datadef:new.browse')}
              </button>
            </div>
            {/* 형식 옵션 — 파일을 고르면 자동 식별로 채워지고, 아래 미리보기로 맞는지 확인한다.
                SAM유형을 골랐으면 유형이 정한 값이라 편집을 잠근다 */}
            <div className={`rounded border border-gray-200 p-2 mb-2 ${samTypeId ? 'opacity-60 pointer-events-none' : ''}`}>
              <div className="flex items-center gap-3 mb-1.5 text-xs text-gray-700">
                <span className="font-semibold text-gray-600">{t('datadef:new.format')}</span>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" checked={samFormat === 'delimited'} onChange={() => setSamFormat('delimited')} />
                  {t('datadef:sam.delimited')}
                </label>
                <label className="flex items-center gap-1 cursor-pointer">
                  <input type="radio" checked={samFormat === 'fixed'} onChange={() => setSamFormat('fixed')} />
                  {t('datadef:sam.fixed')}
                </label>
                <span className="ml-auto text-[0.6875rem] text-gray-400">
                  {sniffing ? t('datadef:new.sniffing')
                    : matchedProfile && !samTypeId ? <span className="text-blue-600">{t('datadef:new.matched', { name: matchedProfile.name })}</span>
                      : t('datadef:new.sniffHint')}
                </span>
              </div>
              <div className="flex flex-wrap items-center gap-3 text-xs text-gray-600">
                {samFormat === 'delimited' && (
                  <label className="flex items-center gap-1">
                    {t('datadef:sam.delimiter')}
                    <select className="border border-gray-300 rounded px-1 py-0.5" value={['\t', ',', '|', ';'].includes(delimiter) ? delimiter : '__other'}
                      onChange={(e) => { if (e.target.value !== '__other') setDelimiter(e.target.value); }}>
                      <option value=",">{t('datadef:new.comma')}</option>
                      <option value="|">{t('datadef:new.pipe')}</option>
                      <option value=";">{t('datadef:new.semicolon')}</option>
                      <option value={'\t'}>{t('datadef:new.tab')}</option>
                      <option value="__other">{t('datadef:new.custom')}</option>
                    </select>
                    <input value={delimiter === '\t' ? '' : delimiter} placeholder={delimiter === '\t' ? 'TAB' : ''}
                      onChange={(e) => setDelimiter(e.target.value)} className="w-10 border border-gray-300 rounded px-1 py-0.5 text-center" />
                  </label>
                )}
                <label className="flex items-center gap-1">
                  <input type="checkbox" checked={hasHeader} onChange={(e) => setHasHeader(e.target.checked)} />
                  {t('datadef:sam.headerYes')}
                </label>
                <label className="flex items-center gap-1">
                  {t('datadef:sam.encoding')}
                  <select className="border border-gray-300 rounded px-1 py-0.5" value={samEncoding} onChange={(e) => setSamEncoding(e.target.value)}>
                    {['UTF-8', 'UTF-8-SIG', 'CP949', 'EUC-KR', 'UTF-16', 'LATIN-1'].map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </label>
              </div>
              {samFormat === 'fixed' && (
                <div className="mt-1.5 grid grid-cols-1 gap-1">
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    {t('datadef:new.widths')}
                    <input value={fieldWidths} onChange={(e) => setFieldWidths(e.target.value)} placeholder={t('datadef:sam.fieldWidthsPh')}
                      className="flex-1 border border-gray-300 rounded px-1.5 py-0.5 font-mono" />
                  </label>
                  <label className="flex items-center gap-1 text-xs text-gray-600">
                    {t('datadef:sam.fieldName')}
                    <input value={fieldNames} onChange={(e) => setFieldNames(e.target.value)} placeholder={t('datadef:new.fieldNamesPh')}
                      className="flex-1 border border-gray-300 rounded px-1.5 py-0.5" />
                  </label>
                  <div className="text-[0.6875rem] text-gray-400">{t('datadef:new.fixedHint')}</div>
                </div>
              )}
            </div>

            {/* 파일 미리보기 — 옵션이 맞으면 표가 반듯하게 나뉜다 */}
            <div className="rounded border border-gray-200 mb-3">
              <div className="px-2 py-1 text-[0.6875rem] font-semibold text-gray-600 bg-gray-50 border-b border-gray-200 flex items-center">
                {t('datadef:new.preview')}
                {preview?.warning && <span className="ml-2 font-normal text-amber-600">⚠ {preview.warning}</span>}
              </div>
              {previewErr ? <div className="p-2 text-xs text-red-500">{previewErr}</div>
                : !filepath.trim() ? <div className="p-2 text-xs text-gray-400">{t('datadef:new.previewEmpty')}</div>
                  : !preview ? <div className="p-2 text-xs text-gray-400">{t('datadef:common.reading')}</div>
                    : (
                      <div className="overflow-auto max-h-48">
                        <table className="text-xs w-full">
                          <thead className="bg-gray-50 sticky top-0">
                            <tr>{preview.columns.map((c, i) => <th key={i} className="px-1.5 py-0.5 text-left font-medium text-gray-700 border-b border-gray-200 whitespace-nowrap">{c}</th>)}</tr>
                          </thead>
                          <tbody>
                            {preview.rows.map((r, i) => (
                              <tr key={i}>{preview.columns.map((_, j) => <td key={j} className="px-1.5 py-0.5 border-b border-gray-100 whitespace-nowrap">{r[j] ?? ''}</td>)}</tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
            </div>
          </>
        ) : (
          <>
            <label className="block text-xs text-gray-500 mb-1">{t('datadef:new.dbConnection')}</label>
            <select
              value={connection}
              onChange={(e) => {
                setConnection(e.target.value);
                setSchema('');
                setTable('');
              }}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-3"
            >
              <option value="">{t('datadef:new.selectConnection')}</option>
              {/* 스토어의 connections 는 {connection_id, conn_type, …} 객체 배열 — 객체를 JSX 자식으로 넣으면 런타임 오류 */}
              {connections.map((c: any) => {
                const id = typeof c === 'string' ? c : c.connection_id;
                const label = typeof c === 'string' ? c : (c.description ? `${c.connection_id} (${c.description})` : c.connection_id);
                return <option key={id} value={id}>{label}</option>;
              })}
            </select>

            <label className="block text-xs text-gray-500 mb-1">{t('datadef:new.schema')}</label>
            <select
              value={schema}
              onChange={(e) => { setSchema(e.target.value); setTable(''); }}
              disabled={!connection}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-3 disabled:bg-gray-100"
            >
              <option value="">{t('datadef:new.selectSchema')}</option>
              {(schemaMap[connection] || []).map((s) => (
                <option key={s} value={s}>{s}{s === defaultSchema[connection] ? t('datadef:new.defaultMark') : ''}</option>
              ))}
            </select>

            <label className="block text-xs text-gray-500 mb-1">{t('datadef:new.table')}</label>
            <select
              value={table}
              onChange={(e) => {
                setTable(e.target.value);
                if (!name.trim()) setName(e.target.value);
              }}
              disabled={!connection || !schema}
              className="w-full text-sm border border-gray-300 rounded px-2 py-1 mb-3 disabled:bg-gray-100"
            >
              <option value="">{t('datadef:new.selectTable')}</option>
              {(tableMap[tableMapKey(connection, schema)] || []).map((tb) => {
                const name = tableName(tb);
                const comment = typeof tb === 'string' ? '' : (tb.comment || '');
                return <option key={name} value={name}>{comment ? `${name} (${comment})` : name}</option>;
              })}
            </select>
          </>
        )}

        {error && <div className="text-xs text-red-500 mb-2">{error}</div>}

        <div className="flex justify-end gap-2 mt-2">
          <button type="button" className="px-3 py-1.5 text-sm border rounded" onClick={onClose}>
            {t('common:ui.cancel')}
          </button>
          <button
            type="button"
            className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded disabled:opacity-50"
            disabled={!canSave || saving}
            onClick={handleSave}
          >
            {saving ? t('dialog:vars.saving') : t('dialog:vars.save')}
          </button>
        </div>
      </div>

      {browserOpen && (
        <ServerFileBrowserDialog
          onClose={() => setBrowserOpen(false)}
          onSelect={(relPath) => {
            setFilepath(relPath);
            if (!name.trim()) setName(relPath.split('/').pop() || relPath);
            setBrowserOpen(false);
            runSniff(relPath);       // 고르는 순간 인코딩·구분자·헤더 자동 식별
          }}
        />
      )}
    </div>
  );
};

export default NewDataDefinitionDialog;
